import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { defineBlock, EditableText, InnerBlocks } from "eddev/blocks"
import DrawSVGPlugin from "gsap/dist/DrawSVGPlugin"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import { Themed } from "@components/atoms/Themed"
import { usePageContext } from "../../contexts/PageContext"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/atoms/Button"
import { CycleText } from "@components/atoms/CycleText"
import { useIntersectionObserver, useWindowSize } from "usehooks-ts"
import { DEFAULT_SETTINGS, FlowgramDisplay, GeneratorSettings } from "@generator/src"
import { DrawingGenerator } from "@components/generator/DrawingGenerator"
import { useAnimationFrame } from "motion/react"
import { useHydrating } from "eddev/routing"
import { twMerge } from "tailwind-merge"
import { cn } from "@utils/tw"
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin"

if (env.client) {
  gsap.registerPlugin(useGSAP)
  gsap.registerPlugin(DrawSVGPlugin)
  gsap.registerPlugin(ScrollTrigger)
  gsap.registerPlugin(ScrollToPlugin)
  gsap.config({
    nullTargetWarn: false,
  })
}

export const meta: BlockMeta = {
  title: "Landing Intro & Featured",
  icon: "block-default",
  tags: ["#page"],
}

export default defineBlock("content/landing-intro-featured", (props) => {
  const ref = useRef<HTMLDivElement>(null)
  const timeline = useRef<GSAPTimeline | null>(null)
  const logoTimeline = useRef<GSAPTimeline | null>(null)
  const [logoDrawn, setLogoDrawn] = useState(false)
  const direction = useRef(1)
  const lastProgress = useRef(0)
  const { setIntroPlaying, setIntroLogoPlaying } = usePageContext()
  const { width } = useWindowSize()
  const hydrating = useHydrating()
  const feat1HasImage = !!props.featured1?.featuredImage?.mediaItemUrl || !!props.featured1?.featuredVideo?.mediaItemUrl
  const feat2HasImage = !!props.featured2?.featuredImage?.mediaItemUrl || !!props.featured2?.featuredVideo?.mediaItemUrl
  const feat3HasImage = !!props.featured3?.featuredImage?.mediaItemUrl || !!props.featured3?.featuredVideo?.mediaItemUrl

  const dimVal = useCallback(
    (desktop: number, mobile?: number) => {
      const isMobile = width < 600 && !hydrating
      const w = isMobile && typeof mobile === "number" ? 384 : 1296
      const valM = typeof mobile === "number" ? mobile : desktop
      return (isMobile ? valM : desktop) / w
    },
    [width, hydrating],
  )

  const dim = useCallback(
    (desktop: number, mobile?: number, applyMargin?: boolean) => {
      const val = dimVal(desktop, mobile)
      return `calc((var(--tw-grid-container) + var(--tw-grid-margin)) * ${val}${applyMargin ? " - var(--tw-grid-margin) * 1.5" : ""})`
    },
    [width, hydrating],
  )

  useEffect(() => {
    setIntroPlaying?.(true)

    return () => {
      setIntroPlaying?.(false)
      setIntroLogoPlaying?.(false)
    }
  }, [setIntroPlaying, setIntroLogoPlaying])

  useGSAP(
    () => {
      if (env.admin) return
      if (timeline.current) {
        timeline.current.revert().kill()
      }
      if (logoTimeline.current) {
        logoTimeline.current.revert().kill()
      }

      const isMobile = width < 600
      let tl: gsap.core.Timeline | null = null
      let logo: gsap.core.Timeline = gsap.timeline({
        paused: true,
        onStart: () => setIntroLogoPlaying?.(true),
        onComplete: () => {
          setIntroLogoPlaying?.(false)
          setLogoDrawn(true)
        },
      })
      logo
        .to(".landing-intro-wrapper", { opacity: 1, duration: 0.01 }, "0")
        .from("#intro_line_one, #mintro_line_one", { drawSVG: "0%", duration: 1 }, "0")
        .from("#intro_line_two, #mintro_line_two", { drawSVG: "0%", duration: 0.5 }, "0+=0.4")
        .from("#intro_line_three, #mintro_line_three", { drawSVG: "0%", duration: 0.5 }, "0+=0.5")
        .from("#intro_line_four, #mintro_line_four", { drawSVG: "0%", duration: 0.5 }, "0+=0.5")
        .from("#intro-logo path, #mintro path", { opacity: 0, stagger: 0.025, duration: 0.15 }, "0")
        .from("#intro-logo path, #mintro path", { x: "-1vw", stagger: 0.05, duration: 0.25 }, "0+=0.0125")
        .to("#intro_line_one, #mintro_line_one", { drawSVG: "100% 100%", duration: 1 }, "0+=1")
        .to("#intro_line_two, #mintro_line_two", { drawSVG: "100% 100%", duration: 0.2 }, "0+=1")
        .to("#intro_line_three, #mintro_line_three", { drawSVG: "100% 100%", duration: 0.35 }, "0+=1.4")
        .to("#intro_line_four, #mintro_line_four", { drawSVG: "100% 100%", duration: 0.35 }, "0+=1.4")
        .to("#intro-logo path, #mintro path", { opacity: 0, stagger: 0.025, duration: 0.15 }, "0+=1.1")
        .to("#intro-logo path, #mintro path", { x: "1vw", stagger: 0.05, duration: 0.25 }, "0+=1.1125")
        .to(".landing-logo-bg", { autoAlpha: 0, duration: 1 }, "0+=1.25")
        .from(".featured1-bg", { autoAlpha: 0, duration: 1 }, "0+=1.25")
        .to(
          ".featured1-line2-desktop, .featured1-line2-mobile-image, .featured1-line2-mobile",
          { drawSVG: "0%", duration: 0.01 },
          "0+=1",
        )
        .addLabel("logoEnd", "0+=1")
        .from(".featured1-line1-desktop", { drawSVG: "0%", duration: 0.5, ease: "none" }, "logoEnd+=0.5")
        .fromTo(".featured1-shape", { drawSVG: "111% 111%" }, { drawSVG: "11% 111%", duration: 1.5 }, "logoEnd+=1")
        .fromTo(
          ".featured1-shape-mobile-image, .featured1-shape-mobile",
          { drawSVG: "111% 111%" },
          { drawSVG: "11% 111%", duration: 1.5 },
          "logoEnd+=1",
        )
        .to(".featured1-line1-desktop", { drawSVG: "100% 100%", duration: 0.5, ease: "none" }, "logoEnd+=1.82")
        .from(
          [
            ".featured1-title",
            ".featured1-category",
            ".featured1-button-title",
            ".featured1-button",
            ".featured1-image",
            ".featured1-rotating-text",
          ],
          { autoAlpha: 0, duration: 0.8, stagger: 0.1 },
          "logoEnd+=1.75",
        )

      logo.timeScale(0.75)

      logoTimeline.current = logo

      if (!logoDrawn) {
        gsap.set(".featured2-content, .featured3-content", { opacity: 0 })
        logo.play()
      } else {
        logoTimeline.current.revert()
        gsap.set(".featured1-bg", { opacity: 1 })
        gsap.set(".featured1-line1-desktop", { drawSVG: "100% 100%" })
        gsap.set(".featured2-shape, .featured2-shape-mobile-image, .featured2-shape-mobile", { drawSVG: "90% 90%" })
        gsap.set(".featured2-content, .featured3-content", { opacity: 0 })

        tl = gsap.timeline({
          scrollTrigger: {
            trigger: ".landing-intro-featured",
            start: "top top",
            end: "bottom-=33%",
            scrub: 0.5,
          },
          onUpdate: () => {
            setIntroPlaying?.(true)
            const progress = tl?.progress() || 0
            direction.current = progress - lastProgress.current >= 0 ? 1 : -1
            lastProgress.current = progress
          },
          onComplete: () => {
            setIntroPlaying?.(false)
          },
        })

        tl.addLabel("one", 0.25)
          .to(
            ".featured2-shape, .featured2-shape-mobile-image, .featured2-shape-mobile",
            { drawSVG: "90% 90%", duration: 0.01 },
            0,
          )
          .to(".featured2-content, .featured3-content", { opacity: 1, duration: 0.01 }, "one")
          .addLabel("two", 1.5)
          .to(".featured1-bg", { autoAlpha: 0, duration: 2 }, "two")
          .to(
            [
              ".featured1-image",
              ".featured1-button",
              ".featured1-button-title",
              ".featured1-category",
              ".featured1-title",
              ".featured1-rotating-text",
            ],
            { autoAlpha: 0, duration: 0.2, stagger: 0.1 },
            "two",
          )
          .from(
            ".featured1-line2-desktop, .featured1-line2-mobile-image, .featured1-line2-mobile",
            { drawSVG: "0%", duration: 1.15, ease: "none" },
            "two",
          )
          .fromTo(".featured1-shape", { drawSVG: "90% 190%" }, { drawSVG: "90% 90%", duration: 4 }, "two+=0.15")
          .fromTo(
            ".featured1-shape-mobile-image, .featured1-shape-mobile",
            { drawSVG: "100% 200%" },
            { drawSVG: "100% 100%", duration: 4 },
            "two+=0.15",
          )
          .fromTo(".featured2-shape", { drawSVG: "90% 90%" }, { drawSVG: "90% 190%", duration: 2 }, "two+=1.5")
          .fromTo(
            ".featured2-shape-mobile-image, .featured2-shape-mobile",
            { drawSVG: "150% 150%" },
            { drawSVG: "150% 250%", duration: 2 },
            "two+=1.25",
          )
          .to(
            ".featured1-line2-desktop, .featured1-line2-mobile-image, .featured1-line2-mobile",
            { drawSVG: "100% 100%", duration: 1.5 },
            "two+=2.585",
          )
          .from(
            [
              ".featured2-title",
              ".featured2-category",
              ".featured2-button-title",
              ".featured2-button",
              ".featured2-image",
            ],
            { autoAlpha: 0, duration: 0.5, stagger: 0.1 },
            "two+=2",
          )
          .from(".featured2-flowgram1", { autoAlpha: 0, duration: 1 }, "two+=1.5")
          .addLabel("three", 6)
          .to(".featured2-bg", { autoAlpha: 0, duration: 2 }, "three")
          .from(
            ".featured2-line-desktop, .featured2-line-mobile-image, .featured2-line-mobile",
            { drawSVG: "0%", duration: 1.5, ease: "none" },
            "three",
          )
          .to(
            [
              ".featured2-image",
              ".featured2-button",
              ".featured2-button-title",
              ".featured2-category",
              ".featured2-title",
            ],
            { autoAlpha: 0, duration: 0.2, stagger: 0.1 },
            "three",
          )
          .to(".featured2-flowgram1", { autoAlpha: 0, duration: 1 }, "three")
          .fromTo(".featured2-shape", { drawSVG: "10% 110%" }, { drawSVG: "110% 110%", duration: 2 }, "three+=0.25")
          .fromTo(
            ".featured2-shape-mobile-image, .featured2-shape-mobile",
            { drawSVG: "95% 195%" },
            { drawSVG: "95% 95%", duration: 1.55 },
            "three+=0.25",
          )
          .fromTo(".featured3-shape", { drawSVG: "110% 110%" }, { drawSVG: "110% 10%", duration: 2 }, "three+=1.5")
          .fromTo(
            ".featured3-shape-mobile-image, .featured3-shape-mobile",
            { drawSVG: "57% 57%" },
            { drawSVG: "57% 157%", duration: 2 },
            "three+=1.5",
          )
          .to(
            ".featured2-line-desktop, .featured2-line-mobile-image, .featured2-line-mobile",
            { drawSVG: "100% 100%", duration: 2 },
            "three+=1.75",
          )
          .from(
            [
              ".featured3-title",
              ".featured3-category",
              ".featured3-button-title",
              ".featured3-button",
              ".featured3-image",
            ],
            { autoAlpha: 0, duration: 0.5, stagger: 0.1 },
            "three+=2",
          )
          .from(".featured2-flowgram2", { autoAlpha: 0, duration: 1 }, "three+=1.5")
          .addLabel("four", 8)
          .to(".featured1-line2-desktop", { drawSVG: "100% 100%", duration: 1 }, "four")

        timeline.current = tl
      }

      gsap.set(".landing-intro-featured-inner", { opacity: 1 })

      return () => {
        tl?.revert().kill()
        logo?.revert().kill()
        timeline.current = null
        setIntroLogoPlaying?.(false)
      }
    },
    {
      scope: ref,
      dependencies: [width, logoDrawn, setIntroLogoPlaying],
    },
  )

  return (
    <div ref={ref}>
      {env.admin ? (
        <div className="w-full py-5 bg-grey-light flex items-center justify-center">
          <p>Landing Intro & Featured (content appears outside of admin panel)</p>
        </div>
      ) : (
        <>
          <div className="landing-intro-featured w-full relative flex items-center justify-center">
            <div className="landing-intro-featured-inner w-full opacity-0">
              <div className="w-full h-[100vh] sticky left-0 top-0">
                {/* featured 3 bg */}
                <Themed
                  theme={props.featured3?.theme}
                  className="featured3-bg w-full absolute left-0 top-0 h-[100vh] themed-bg"
                >
                  {props.featured3?.backgroundVideo?.mediaItemUrl && (
                    <video
                      src={props.featured3.backgroundVideo.mediaItemUrl}
                      autoPlay
                      muted
                      playsInline
                      loop
                      className="w-full h-full object-cover"
                    ></video>
                  )}
                  {!props.featured3?.backgroundVideo?.mediaItemUrl && props.featured3?.backgroundImage && (
                    <ResponsiveImage
                      {...props.featured3?.backgroundImage}
                      objectFit="cover"
                      className="w-full h-full"
                    />
                  )}
                  {(props.featured3?.backgroundImage?.mediaItemUrl ||
                    props.featured3?.backgroundVideo?.mediaItemUrl) && (
                    <div className="bg-black opacity-50 absolute w-full h-full left-0 top-0" />
                  )}
                </Themed>

                {/* featured 2 bg */}
                <Themed
                  theme={props.featured2?.theme}
                  className="featured2-bg w-full absolute left-0 top-0 h-[100vh] themed-bg"
                >
                  {props.featured2?.backgroundVideo?.mediaItemUrl && (
                    <video
                      src={props.featured2.backgroundVideo.mediaItemUrl}
                      autoPlay
                      muted
                      playsInline
                      loop
                      className="w-full h-full object-cover"
                    ></video>
                  )}
                  {!props.featured2?.backgroundVideo?.mediaItemUrl && props.featured2?.backgroundImage && (
                    <ResponsiveImage
                      {...props.featured2?.backgroundImage}
                      objectFit="cover"
                      className="w-full h-full"
                    />
                  )}
                  {(props.featured2?.backgroundImage?.mediaItemUrl ||
                    props.featured2?.backgroundVideo?.mediaItemUrl) && (
                    <div className="bg-black opacity-50 absolute w-full h-full left-0 top-0" />
                  )}
                </Themed>

                {/* featured 1 bg */}
                <Themed
                  theme={props.featured1?.theme}
                  className="featured1-bg w-full absolute left-0 top-0 h-[100vh] themed-bg"
                >
                  {props.featured1?.backgroundVideo?.mediaItemUrl && (
                    <video
                      src={props.featured1.backgroundVideo.mediaItemUrl}
                      autoPlay
                      playsInline
                      muted
                      loop
                      className="w-full h-full object-cover"
                    ></video>
                  )}
                  {!props.featured1?.backgroundVideo?.mediaItemUrl && props.featured1?.backgroundImage && (
                    <ResponsiveImage
                      {...props.featured1?.backgroundImage}
                      objectFit="cover"
                      className="w-full h-full"
                    />
                  )}
                  {(props.featured1?.backgroundImage?.mediaItemUrl ||
                    props.featured1?.backgroundVideo?.mediaItemUrl) && (
                    <div className="bg-black opacity-50 absolute w-full h-full left-0 top-0" />
                  )}
                </Themed>
              </div>

              {/* featured 1 content */}
              <div className="featured1-content w-grid mx-auto h-[85vh] -mt-[85vh] sm:h-[100vh] sm:-mt-[100vh] relative flex items-center justify-center">
                <div className="w-full relative" style={{ height: dim(776, 776) }}>
                  {/* featured1-line1-desktop */}
                  <svg
                    viewBox="0 0 2 359"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute hidden sm:block"
                    style={{
                      width: dim(2, 1),
                      height: dim(359, 200),
                      left: dim(528, 24, true),
                      top: dim(-111, 120),
                    }}
                  >
                    <path className="featured1-line1-desktop" d="M1 0V359" stroke="#E5E8DB" strokeWidth="2" />
                  </svg>

                  {/* featured1-shape */}
                  <svg
                    viewBox="0 0 730 514"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute hidden sm:block"
                    style={{
                      width: dim(730, 0),
                      height: dim(514, 0),
                      left: dim(528, 0, true),
                      top: dim(132, 0),
                    }}
                  >
                    <path
                      d="M130 257H49C22.4903 257 1 235.51 1 209V49C1 22.4903 22.4903 1 49 1H681C707.51 1 729 22.4903 729 49V465C729 491.51 707.51 513 681 513H226C199.49 513 178 491.51 178 465V305C178 278.49 156.51 257 130 257Z"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured1-shape"
                    />
                  </svg>

                  {/* featured1-shape-mobile-image */}
                  {feat1HasImage && (
                    <svg
                      viewBox="0 0 338 482"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute sm:hidden"
                      style={{
                        width: dim(0, 338),
                        height: dim(0, 482),
                        left: dim(0, 24, true),
                        top: dim(0, 93),
                      }}
                    >
                      <path
                        d="M65 449V417C65 399.327 50.6731 385 33 385C15.3269 385 1 370.673 1 353V33C1 15.3269 15.3269 1 33 1H305C322.673 1 337 15.3269 337 33V449C337 466.673 322.673 481 305 481H97C79.3269 481 65 466.673 65 449Z"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                        className="featured1-shape-mobile-image"
                      />
                    </svg>
                  )}

                  {/* featured1-shape-mobile */}
                  {!feat1HasImage && (
                    <svg
                      viewBox="0 0 338 338"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute sm:hidden"
                      style={{
                        width: dim(0, 338),
                        height: dim(0, 338),
                        left: dim(0, 24, true),
                        top: dim(0, 93),
                      }}
                    >
                      <path
                        d="M65 305V273.491C65 255.818 50.6731 241.491 33 241.491C15.3269 241.491 1 227.164 1 209.491V33C1 15.3269 15.3269 1 33 1H305C322.673 1 337 15.3269 337 33V305C337 322.673 322.673 337 305 337H97C79.3269 337 65 322.673 65 305Z"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                        className="featured1-shape-mobile"
                      />
                    </svg>
                  )}

                  {/* featured1-line2-desktop */}
                  <svg
                    viewBox="0 0 2 537"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute hidden sm:block"
                    style={{
                      width: dim(2, 1),
                      height: dim(537, 300),
                      left: dim(704.95, 24, true),
                      top: dim(502, 120),
                    }}
                  >
                    <path className="featured1-line2-desktop" d="M1 0V536.5" stroke="#E5E8DB" strokeWidth="2" />
                  </svg>

                  {/* featured1-line2-mobile-image */}
                  {feat1HasImage && (
                    <svg
                      viewBox="0 0 66 459"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute sm:hidden"
                      style={{
                        width: dim(0, 66),
                        height: dim(0, 459),
                        left: dim(0, 24, true),
                        top: dim(0, 510),
                      }}
                    >
                      <path
                        d="M65 0V209.5C65 222.755 54.2548 233.5 41 233.5H25C11.7452 233.5 1 244.245 1 257.5V459"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                        className="featured1-line2-mobile-image"
                      />
                    </svg>
                  )}

                  {/* featured1-line2-mobile */}
                  {!feat1HasImage && (
                    <svg
                      viewBox="0 0 66 683"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute sm:hidden"
                      style={{
                        width: dim(0, 66),
                        height: dim(0, 683),
                        left: dim(0, 24, true),
                        top: dim(0, 366),
                      }}
                    >
                      <path
                        d="M65 0V353.5C65 366.755 54.2548 377.5 41 377.5H25C11.7452 377.5 1 388.245 1 401.5V683"
                        stroke="#E5E8DB"
                        strokeWidth="2"
                        className="featured1-line2-mobile"
                      />
                    </svg>
                  )}

                  <p
                    className="featured1-title absolute text-white"
                    style={{
                      width: dim(650, 288),
                      left: dim(568, 48, true),
                      top: dim(176, feat1HasImage ? 320 : 120),
                    }}
                  >
                    <span className="type-title-m leading-[90%]" style={{ fontSize: dim(52, 27) }}>
                      {props.featured1?.title}
                    </span>

                    <span
                      className="featured1-category block type-label-s-caps text-white leading-none"
                      style={{
                        marginTop: dim(20, 24),
                      }}
                    >
                      {props.featured1?.category}
                    </span>
                  </p>

                  {feat1HasImage && (
                    <div
                      className="featured1-image absolute overflow-hidden rounded-lg"
                      style={{
                        left: dim(976, 48, true),
                        top: dim(444, 120),
                        width: dim(240, 288),
                        height: dim(160, 176),
                      }}
                    >
                      {props.featured1?.featuredVideo?.mediaItemUrl && (
                        <video
                          src={props.featured1.featuredVideo.mediaItemUrl}
                          autoPlay
                          muted
                          playsInline
                          loop
                          className="w-full h-full object-cover"
                        ></video>
                      )}
                      {!props.featured1?.featuredVideo?.mediaItemUrl && props.featured1?.featuredImage && (
                        <ResponsiveImage
                          {...props.featured1?.featuredImage}
                          objectFit="cover"
                          className="w-full h-full"
                        />
                      )}
                    </div>
                  )}

                  <div
                    className="absolute flex flex-col"
                    style={{
                      left: dim(746, 112, true),
                      top: dim(536, feat1HasImage ? 480 : 335),
                      gap: dim(20, 16),
                    }}
                  >
                    <p
                      className="featured1-button-title type-body-xl text-white leading-none"
                      style={{
                        fontSize: dim(20, 18),
                      }}
                    >
                      {props.featured1?.buttonTitle || <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }} />}
                    </p>
                    <div className="featured1-button">
                      <Button
                        href={props.featured1?.buttonLink?.url || ""}
                        target={props.featured1?.buttonLink?.target || "_self"}
                        variant="light"
                        radius="sm"
                        className="!flex !h-auto !leading-none"
                        style={{
                          fontSize: dim(14, 14),
                          padding: `${dim(10, 10)} ${dim(24, 24)}`,
                        }}
                      >
                        {props.featured1?.buttonLink?.title}
                      </Button>
                    </div>
                  </div>
                </div>
                <div
                  className={cn("featured1-rotating-text absolute w-full pointer-events-none text-white")}
                  style={{
                    left: dim(40, 24, true),
                    bottom: dim(40, 0),
                  }}
                >
                  <div
                    className="type-title-m leading-none"
                    style={{
                      fontSize: dim(32, 24),
                    }}
                  >
                    {props.rotatingText?.primaryText}
                  </div>
                  <div
                    className="w-full type-title-m relative opacity-[0.55] leading-none"
                    style={{
                      fontSize: dim(32, 24),
                    }}
                  >
                    <CycleText
                      lines={props.rotatingText?.rotatingLines?.map((line) => line?.line!) ?? ["for your people."]}
                    />
                  </div>
                </div>
              </div>

              {/* featured 2 content */}
              <div
                className="featured2-content w-grid mx-auto relative"
                style={{
                  height: dim(776, 776),
                  marginTop: `calc(-0.5 * (100vh - ((var(--tw-grid-container) + var(--tw-grid-margin)) * ${dimVal(776, 776)})))`,
                  maxHeight: "100vh",
                }}
              >
                {/* featured2-shape */}
                <svg
                  viewBox="0 0 668 514"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute hidden sm:block"
                  style={{
                    width: dim(668, 288),
                    height: dim(514, 202),
                    left: dim(39, 48, true),
                    top: dim(132, 120),
                  }}
                >
                  <path
                    className="featured2-shape"
                    d="M619 257H610C583.49 257 562 278.49 562 305V465C562 491.51 540.51 513 514 513H49C22.4903 513 1 491.51 1 465V49C1 22.4903 22.4903 1 49 1H619C645.51 1 667 22.4903 667 49V209C667 235.51 645.51 257 619 257Z"
                    stroke="#E5E8DB"
                    strokeWidth="2"
                  />
                </svg>

                {/* featured2-shape-mobile-image */}
                {feat2HasImage && (
                  <svg
                    viewBox="0 0 338 482"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute sm:hidden"
                    style={{
                      width: dim(0, 338),
                      height: dim(0, 482),
                      left: dim(0, 24, true),
                      top: dim(0, 160),
                    }}
                  >
                    <path
                      d="M273 385H305C322.673 385 337 370.673 337 353V33C337 15.3269 322.673 1 305 1H33C15.3269 1 1 15.3269 1 33V449C1 466.673 15.3269 481 33 481H209C226.673 481 241 466.673 241 449V417C241 399.327 255.327 385 273 385Z"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured2-shape-mobile-image"
                    />
                  </svg>
                )}

                {/* featured2-shape-mobile */}
                {!feat2HasImage && (
                  <svg
                    viewBox="0 0 338 338"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute sm:hidden"
                    style={{
                      width: dim(0, 338),
                      height: dim(0, 338),
                      left: dim(0, 24, true),
                      top: dim(0, 220),
                    }}
                  >
                    <path
                      d="M273 245.364H305C322.673 245.364 337 231.037 337 213.364V33C337 15.3269 322.673 1 305 1H33C15.3269 1 1 15.3269 1 33V305C1 322.673 15.3269 337 33 337H209C226.673 337 241 322.673 241 305V277.364C241 259.691 255.327 245.364 273 245.364Z"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured2-shape-mobile"
                    />
                  </svg>
                )}

                {/* featured2-line-desktop */}
                <svg
                  viewBox="0 0 2 565"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute hidden sm:block"
                  style={{
                    width: dim(2, 1),
                    height: dim(565, 316),
                    left: dim(600, 24, true),
                    top: dim(497, 120),
                  }}
                >
                  <path className="featured2-line-desktop" d="M1 0V564.5" stroke="#E5E8DB" strokeWidth="2" />
                </svg>

                {/* featured2-line-mobile-image */}
                {feat2HasImage && (
                  <svg
                    viewBox="0 0 98 514"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute sm:hidden"
                    style={{
                      width: dim(0, 98),
                      height: dim(0, 514),
                      left: dim(0, 264, true),
                      top: dim(0, 580),
                    }}
                  >
                    <path
                      d="M1 0V195C1 208.255 11.7452 219 25 219H73C86.2548 219 97 229.745 97 243V513.5"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured2-line-mobile-image"
                    />
                  </svg>
                )}

                {/* featured2-line-mobile */}
                {!feat2HasImage && (
                  <svg
                    viewBox="0 0 98 562"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute sm:hidden"
                    style={{
                      width: dim(0, 98),
                      height: dim(0, 562),
                      left: dim(0, 264, true),
                      top: dim(0, 509),
                    }}
                  >
                    <path
                      d="M1 0V243C1 256.255 11.7452 267 25 267H73C86.2548 267 97 277.745 97 291V561.5"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured2-line-mobile"
                    />
                  </svg>
                )}

                <div
                  className="featured2-title absolute"
                  style={{
                    left: dim(79, 48, true),
                    top: dim(176, feat2HasImage ? 388 : 245),
                    width: dim(585, 288),
                  }}
                >
                  <p
                    className="leading-[90%] type-title-m line-clamp-4 text-white"
                    style={{
                      fontSize: dim(52, 27),
                    }}
                  >
                    {props.featured2?.title}
                  </p>

                  <span
                    className="featured2-category type-label-s-caps block text-white leading-none"
                    style={{
                      marginTop: dim(20, 24),
                    }}
                  >
                    {props.featured2?.category}
                  </span>
                </div>

                {feat2HasImage && (
                  <div
                    className="featured2-image absolute overflow-hidden rounded-lg"
                    style={{
                      left: dim(320, 48, true),
                      top: dim(444, 188),
                      width: dim(240, 288),
                      height: dim(160, 176),
                    }}
                  >
                    {props.featured2?.featuredVideo?.mediaItemUrl && (
                      <video
                        src={props.featured2.featuredVideo.mediaItemUrl}
                        autoPlay
                        playsInline
                        muted
                        loop
                        className="w-full h-full object-cover"
                      ></video>
                    )}
                    {!props.featured2?.featuredVideo?.mediaItemUrl && props.featured2?.featuredImage && (
                      <ResponsiveImage
                        {...props.featured2?.featuredImage}
                        objectFit="cover"
                        className="w-full h-full"
                      />
                    )}
                  </div>
                )}

                <div
                  className="absolute flex flex-col"
                  style={{
                    left: dim(79, 48, true),
                    top: dim(530, feat2HasImage ? 547 : 465),
                    gap: dim(20, 16),
                  }}
                >
                  <p
                    className="featured2-button-title type-body-xl text-white leading-none"
                    style={{
                      fontSize: dim(20, 18),
                    }}
                  >
                    {props.featured2?.buttonTitle || <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }} />}
                  </p>
                  <div className="featured2-button">
                    <Button
                      href={props.featured2?.buttonLink?.url || ""}
                      target={props.featured2?.buttonLink?.target || "_self"}
                      variant="light"
                      radius="sm"
                      className="!flex !h-auto !leading-none"
                      style={{
                        fontSize: dim(14, 14),
                        padding: `${dim(10, 10)} ${dim(24, 24)}`,
                      }}
                    >
                      {props.featured2?.buttonLink?.title}
                    </Button>
                  </div>
                </div>

                {/* featured 2 flowgram desktop */}
                <div
                  className="featured2-flowgram1 absolute hidden sm:block"
                  style={{
                    width: dim(512, 336),
                    height: dim(512, 336),
                    left: dim(744, 24, true),
                    top: dim(132, 224),
                  }}
                >
                  <Generator />
                </div>
              </div>

              {/* featured 3 content */}
              <div
                className="featured3-content w-grid mx-auto relative"
                style={{
                  height: dim(776, 776),
                  maxHeight: "100vh",
                }}
              >
                {/* featured3-shape */}
                <svg
                  viewBox="0 0 658 514"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute hidden sm:block"
                  style={{
                    width: dim(658, 288),
                    height: dim(514, 202),
                    left: dim(600, 48, true),
                    top: dim(133, 120),
                  }}
                >
                  <path
                    className="featured3-shape"
                    d="M1 332V257V49C1 22.4903 22.4903 1 49 1H609C635.51 1 657 22.4903 657 49V365C657 391.51 635.51 413 609 413H377C350.49 413 329 434.49 329 461V465C329 491.51 307.51 513 281 513H49C22.4903 513 1 491.51 1 465V332Z"
                    stroke="#E5E8DB"
                    strokeWidth="2"
                  />
                </svg>

                {/* featured3-shape-mobile-image */}
                {feat3HasImage && (
                  <svg
                    viewBox="0 0 338 482"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute sm:hidden"
                    style={{
                      width: dim(0, 338),
                      height: dim(0, 482),
                      left: dim(0, 24, true),
                      top: dim(0, 148),
                    }}
                  >
                    <path
                      d="M65 449V417C65 399.327 50.6731 385 33 385C15.3269 385 1 370.673 1 353V33C1 15.3269 15.3269 1 33 1H305C322.673 1 337 15.3269 337 33V449C337 466.673 322.673 481 305 481H97C79.3269 481 65 466.673 65 449Z"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured3-shape-mobile-image"
                    />
                  </svg>
                )}

                {/* featured3-shape-mobile */}
                {!feat3HasImage && (
                  <svg
                    viewBox="0 0 338 338"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute sm:hidden"
                    style={{
                      width: dim(0, 338),
                      height: dim(0, 338),
                      left: dim(0, 24, true),
                      top: dim(0, 230),
                    }}
                  >
                    <path
                      d="M65 305V273.491C65 255.818 50.6731 241.491 33 241.491C15.3269 241.491 1 227.164 1 209.491V33C1 15.3269 15.3269 1 33 1H305C322.673 1 337 15.3269 337 33V305C337 322.673 322.673 337 305 337H97C79.3269 337 65 322.673 65 305Z"
                      stroke="#E5E8DB"
                      strokeWidth="2"
                      className="featured3-shape-mobile"
                    />
                  </svg>
                )}

                <div
                  className="featured3-title absolute"
                  style={{
                    left: dim(640, 48, true),
                    top: dim(177, feat3HasImage ? 374 : 255),
                    width: dim(585, 288),
                  }}
                >
                  <p
                    className="leading-[90%] type-title-m line-clamp-4 text-white"
                    style={{
                      fontSize: dim(52, 27),
                    }}
                  >
                    {props.featured3?.title}
                  </p>

                  <span
                    className="featured3-category type-label-s-caps block text-white leading-none"
                    style={{
                      marginTop: dim(20, 24),
                    }}
                  >
                    {props.featured3?.category}
                  </span>
                </div>

                {feat3HasImage && (
                  <div
                    className="featured3-image absolute overflow-hidden rounded-lg"
                    style={{
                      left: dim(976, 48, true),
                      top: dim(345, 174),
                      width: dim(240, 288),
                      height: dim(160, 176),
                    }}
                  >
                    {props.featured3?.featuredVideo?.mediaItemUrl && (
                      <video
                        src={props.featured3.featuredVideo.mediaItemUrl}
                        autoPlay
                        playsInline
                        muted
                        loop
                        className="w-full h-full object-cover"
                      ></video>
                    )}
                    {!props.featured3?.featuredVideo?.mediaItemUrl && props.featured3?.featuredImage && (
                      <ResponsiveImage
                        {...props.featured3?.featuredImage}
                        objectFit="cover"
                        className="w-full h-full"
                      />
                    )}
                  </div>
                )}

                <div
                  className="absolute flex flex-col"
                  style={{
                    left: dim(640, 112, true),
                    top: dim(537, feat3HasImage ? 536 : 475),
                    gap: dim(20, 16),
                  }}
                >
                  <p
                    className="featured3-button-title type-body-xl text-white leading-none"
                    style={{
                      fontSize: dim(20, 18),
                    }}
                  >
                    {props.featured3?.buttonTitle || <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }} />}
                  </p>
                  <div className="featured3-button">
                    <Button
                      href={props.featured3?.buttonLink?.url || ""}
                      target={props.featured3?.buttonLink?.target || "_self"}
                      variant="light"
                      radius="sm"
                      className="!flex !h-auto !leading-none"
                      style={{
                        fontSize: dim(14, 14),
                        padding: `${dim(10, 10)} ${dim(24, 24)}`,
                      }}
                    >
                      {props.featured3?.buttonLink?.title}
                    </Button>
                  </div>
                </div>

                {/* featured 3 flowgram desktop */}
                <div
                  className="featured2-flowgram2 absolute hidden sm:block"
                  style={{
                    width: dim(512, 336),
                    height: dim(512, 336),
                    left: dim(39, 24, true),
                    top: dim(132, 224),
                  }}
                >
                  <Generator />
                </div>
              </div>

              <div className="landing-intro-wrapper w-full h-[100vh] absolute left-0 top-0 pointer-events-none opacity-0">
                <Themed
                  theme="navy"
                  className="landing-logo-bg themed-bg absolute top-0 left-0 w-full h-full pointer-events-none"
                >
                  &nbsp;
                </Themed>

                {/* intro linework */}
                <svg
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute left-0 top-1/2 w-[100vw] -translate-y-1/2 hidden sm:block"
                  viewBox="0, 0, 2592, 1552"
                  style={{
                    aspectRatio: "2592 / 1552",
                  }}
                >
                  <g id="intro-logo">
                    <path
                      d="M913.928,827.996 C877.618,827.996 850.111,798.581 850.111,760.089 C850.111,721.596 877.618,692.724 913.928,692.724 C939.512,692.724 961.518,707.843 967.563,729.561 L1003.316,729.561 C996.993,689.981 957.939,661.387 913.928,661.387 C858.913,661.387 817.103,704 817.103,760.089 C817.103,816.177 858.913,859.346 913.928,859.346 C959.317,859.346 996.171,830.474 1003.872,788.683 L967.841,788.683 C962.062,812.056 940.056,827.996 913.928,827.996 z"
                      fill="#E5E8DB"
                      id="logo-c"
                    />
                    <path
                      d="M1087.98,719.396 C1048.37,719.396 1018.107,749.634 1018.107,789.504 C1018.107,829.373 1048.37,859.345 1087.98,859.345 C1127.591,859.345 1157.565,829.095 1157.565,789.504 C1157.565,749.912 1127.035,719.396 1087.98,719.396 z M1087.98,829.917 C1065.417,829.917 1047.814,812.321 1047.814,789.504 C1047.814,766.686 1065.417,748.534 1087.98,748.534 C1110.542,748.534 1127.856,766.408 1127.856,789.504 C1127.856,812.599 1109.985,829.917 1087.98,829.917 z"
                      fill="#E5E8DB"
                      id="logo-o"
                    />
                    <path
                      d="M1239.594,719.396 C1202.462,719.396 1176.599,745.791 1176.599,784.283 L1176.599,856.59 L1207.406,856.59 L1207.406,784.283 C1207.406,762.565 1220.056,748.812 1239.594,748.812 C1259.133,748.812 1271.78,762.565 1271.78,784.283 L1271.78,856.59 L1302.31,856.59 L1302.31,784.283 C1302.31,745.791 1276.724,719.396 1239.594,719.396 z"
                      fill="#E5E8DB"
                      id="logo-n"
                    />
                    <path
                      d="M1371.724,686.128 L1341.194,686.128 L1341.194,722.143 L1315.15,722.143 L1315.15,750.979 L1341.194,750.979 L1341.194,825.255 C1341.194,842.284 1355.004,856.085 1372.038,856.085 L1406.958,856.085 L1406.958,826.935 L1371.724,826.935 L1371.724,751.281 L1406.958,751.281 L1406.958,722.131 L1371.724,722.131 L1371.724,686.116 L1371.724,686.128 z"
                      fill="#E5E8DB"
                      id="logo-t"
                    />
                    <path
                      d="M1490.636,719.396 C1451.024,719.396 1420.761,749.634 1420.761,789.504 C1420.761,829.373 1451.024,859.345 1490.636,859.345 C1530.245,859.345 1560.218,829.095 1560.218,789.504 C1560.218,749.912 1529.69,719.396 1490.636,719.396 z M1490.636,829.917 C1468.073,829.917 1450.47,812.321 1450.47,789.504 C1450.47,766.686 1468.073,748.534 1490.636,748.534 C1513.198,748.534 1530.512,766.408 1530.512,789.504 C1530.512,812.599 1512.641,829.917 1490.636,829.917 z"
                      fill="#E5E8DB"
                      id="logo-o2"
                    />
                    <path
                      d="M1675.024,794.461 C1675.024,816.179 1662.098,829.932 1642.839,829.932 C1623.577,829.932 1610.651,816.179 1610.651,794.461 L1610.651,722.154 L1579.843,722.154 L1579.843,794.461 C1579.843,832.954 1605.694,859.348 1642.839,859.348 C1679.981,859.348 1705.555,832.954 1705.555,794.461 L1705.555,722.154 L1675.024,722.154 L1675.024,794.461 z"
                      fill="#E5E8DB"
                      id="logo-u"
                    />
                    <path
                      d="M1756.48,722.154 C1741.789,722.154 1729.879,734.059 1729.879,748.742 L1729.879,856.593 L1760.409,856.593 L1760.409,751.304 L1806.9,751.304 L1806.9,722.167 L1756.48,722.167 L1756.48,722.154 z"
                      fill="#E5E8DB"
                      id="logo-r"
                    />
                  </g>
                  <path
                    d="M2605.708,1008.265 C2605.708,1008.265 2488.474,1008.265 2437.987,1008.265 C2387.499,1008.265 2309.629,1050.053 2309.629,1145.038 C2309.629,1240.022 2310.484,1233.177 2310.484,1263.983 C2310.484,1294.789 2288.839,1393.099 2163.904,1393.099 C2160.115,1393.099 2137.906,1392.93 2135.265,1392.93 C2091.107,1392.93 2008.833,1416.7 2008.833,1559.833"
                    fillOpacity="0"
                    stroke="#FFFFFF"
                    strokeWidth="4"
                    id="intro_line_four"
                  />
                  <path
                    d="M2600.573,1008.941 C2600.573,1008.941 2307.917,1008.941 2230.902,1008.941 C2153.887,1008.941 2008.834,1049.197 2008.834,1243.445 C2008.834,1437.694 2008.834,1564.34 2008.834,1564.34"
                    fillOpacity="0"
                    stroke="#FFFFFF"
                    strokeWidth="4"
                    id="intro_line_three"
                  />
                  <path
                    d="M-7.167,338.092 C-7.167,338.092 150.224,337.254 199.285,337.254 C311.037,337.254 416.833,413.667 416.833,552.941 C416.833,746.196 418.433,766.202 417.337,799.8 C414.611,883.387 482.752,1008.941 628.704,1008.941 C703.53,1008.941 818.915,1008.941 818.915,1008.941"
                    fillOpacity="0"
                    stroke="#FFFFFF"
                    strokeWidth="4"
                    id="intro_line_two"
                  />
                  <path
                    d="M-7.167,1009.167 C-7.167,1009.167 1753.812,1007.298 1798.331,1008.941 C1852.844,1007.858 2009.114,1048.743 2008.833,1230.5 C2008.833,1467.833 2008.833,1559.833 2008.833,1559.833"
                    fillOpacity="0"
                    stroke="#FFFFFF"
                    strokeWidth="4"
                    id="intro_line_one"
                  />
                </svg>

                {/* intro linework mobile specific */}
                <svg
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0, 0, 384, 743"
                  className="absolute left-0 top-1/2 w-[100vw] -translate-y-1/2 sm:hidden"
                  style={{
                    aspectRatio: "384 / 743",
                  }}
                >
                  <g id="mintro">
                    <path
                      d="M111.565,384.915 C104.228,384.915 98.67,378.971 98.67,371.193 C98.67,363.416 104.228,357.582 111.565,357.582 C116.734,357.582 121.181,360.637 122.402,365.025 L129.627,365.025 C128.349,357.027 120.458,351.25 111.565,351.25 C100.448,351.25 92,359.86 92,371.193 C92,382.527 100.448,391.25 111.565,391.25 C120.736,391.25 128.183,385.416 129.739,376.971 L122.459,376.971 C121.291,381.694 116.844,384.915 111.565,384.915 z"
                      fill="#E5E8DB"
                      id="mlogo-c"
                    />
                    <path
                      d="M146.734,362.971 C138.73,362.971 132.615,369.081 132.615,377.137 C132.615,385.194 138.73,391.25 146.734,391.25 C154.738,391.25 160.794,385.137 160.794,377.137 C160.794,369.137 154.625,362.971 146.734,362.971 z M146.734,385.303 C142.175,385.303 138.618,381.748 138.618,377.137 C138.618,372.527 142.175,368.859 146.734,368.859 C151.293,368.859 154.792,372.471 154.792,377.137 C154.792,381.804 151.181,385.303 146.734,385.303 z"
                      fill="#E5E8DB"
                      id="mlogo-o"
                    />
                    <path
                      d="M177.37,362.971 C169.867,362.971 164.642,368.305 164.642,376.082 L164.642,390.693 L170.867,390.693 L170.867,376.082 C170.867,371.694 173.422,368.915 177.37,368.915 C181.318,368.915 183.874,371.694 183.874,376.082 L183.874,390.693 L190.043,390.693 L190.043,376.082 C190.043,368.305 184.873,362.971 177.37,362.971 z"
                      fill="#E5E8DB"
                      id="mlogo-n"
                    />
                    <path
                      d="M204.068,356.249 L197.899,356.249 L197.899,363.526 L192.637,363.526 L192.637,369.353 L197.899,369.353 L197.899,384.361 C197.899,387.802 200.689,390.591 204.132,390.591 L211.187,390.591 L211.187,384.701 L204.068,384.701 L204.068,369.414 L211.187,369.414 L211.187,363.524 L204.068,363.524 L204.068,356.246 L204.068,356.249 z"
                      fill="#E5E8DB"
                      id="mlogo-t"
                    />
                    <path
                      d="M228.095,362.971 C220.092,362.971 213.977,369.081 213.977,377.137 C213.977,385.194 220.092,391.25 228.095,391.25 C236.099,391.25 242.156,385.137 242.156,377.137 C242.156,369.137 235.987,362.971 228.095,362.971 z M228.095,385.303 C223.537,385.303 219.979,381.748 219.979,377.137 C219.979,372.527 223.537,368.859 228.095,368.859 C232.654,368.859 236.153,372.471 236.153,377.137 C236.153,381.804 232.542,385.303 228.095,385.303 z"
                      fill="#E5E8DB"
                      id="mlogo-o2"
                    />
                    <path
                      d="M265.352,378.139 C265.352,382.527 262.741,385.306 258.849,385.306 C254.957,385.306 252.345,382.527 252.345,378.139 L252.345,363.529 L246.12,363.529 L246.12,378.139 C246.12,385.917 251.344,391.25 258.849,391.25 C266.354,391.25 271.521,385.917 271.521,378.139 L271.521,363.529 L265.352,363.529 L265.352,378.139 z"
                      fill="#E5E8DB"
                      id="mlogo-u"
                    />
                    <path
                      d="M281.811,363.529 C278.843,363.529 276.437,365.934 276.437,368.901 L276.437,390.693 L282.605,390.693 L282.605,369.419 L291.999,369.419 L291.999,363.531 L281.811,363.531 L281.811,363.529 z"
                      fill="#E5E8DB"
                      id="mlogo-r"
                    />
                  </g>
                  <path
                    d="M-95.5,426.75 L178.5,426.75 C206.667,426.75 229.5,449.583 229.5,477.75 L229.5,743.5"
                    fillOpacity="0"
                    stroke="#E5E8DB"
                    strokeWidth="2"
                    id="mintro_line_one"
                  />
                  <path
                    d="M-95.5,263.75 L-6.5,263.75 C21.667,263.75 44.5,286.583 44.5,314.75 L44.5,375.75 C44.5,403.917 67.334,426.75 95.5,426.75 L178.5,426.75 C206.667,426.75 229.5,449.583 229.5,477.75 L229.5,743.5"
                    fillOpacity="0"
                    stroke="#E5E8DB"
                    strokeWidth="2"
                    id="mintro_line_two"
                  />
                  <path
                    d="M480.5,426.75 L280.5,426.75 C252.333,426.75 229.5,449.583 229.5,477.75 L229.5,743.5"
                    fillOpacity="0"
                    stroke="#E5E8DB"
                    strokeWidth="2"
                    id="mintro_line_three"
                  />
                  <path
                    d="M480.5,426.75 L335.464,426.75 C317.791,426.75 303.464,441.077 303.464,458.75 L303.464,487.75 C303.464,505.423 289.137,519.75 271.464,519.75 L261.5,519.75 C243.827,519.75 229.5,534.077 229.5,551.75 L229.5,743.5"
                    fillOpacity="0"
                    stroke="#E5E8DB"
                    strokeWidth="2"
                    id="mintro_line_four"
                  />
                </svg>
              </div>
            </div>
          </div>
          {/* flowgram mobile */}
          <MobileFlowgram />
        </>
      )}
    </div>
  )
})

const MobileFlowgram = () => {
  const { isIntersecting, ref } = useIntersectionObserver({
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
  })

  return (
    <div ref={ref}>
      <Themed
        theme="red"
        className="featured2-flowgram3 sm:hidden themed-bg themed-fg w-full py-[30vw] px-6 flex items-center justify-center"
      >
        <div className="w-full aspect-square" style={{ opacity: isIntersecting ? 1 : 0 }}>
          <Generator />
        </div>
      </Themed>
    </div>
  )
}

function Generator() {
  const ref = useRef<HTMLDivElement>(null)

  const settings = useMemo(() => {
    const settings: GeneratorSettings = {
      ...DEFAULT_SETTINGS,
      cols: 8,
      rows: 8,
      density: 8,
      seed: Math.random(),
      lineOffset: 0,
      lineWidth: 3,
      scheme: "neutral",
      invertScheme: true,
      outline: true,
      autoWarpOn: false,
      mouseOn: false,
      exportPadding: 0,
      renderMode: "draw",
    }
    return settings
  }, [])

  const [enabled, setEnabled] = useState(false)

  useAnimationFrame(() => {
    const parent = ref.current?.parentElement
    if (parent) {
      setEnabled(Number(parent.style.opacity) > 0.01)
    }
  })

  return <div ref={ref}>{enabled && <DrawingGenerator settings={settings} />}</div>
}
